import { useEffect, useState, useRef } from 'react'
import { Typography, MenuItem, useTheme } from '@mui/material'
import BoxWithBackground from 'components/atoms/container/BoxWithBackground'
import RowStack from 'components/atoms/container/RowStack'
import AudioPlayer, { RHAP_UI } from 'react-h5-audio-player'
import 'react-h5-audio-player/lib/styles.css'
import {
  logScreenAction,
  trackingSubScreenKey
} from 'services/trackingScreenActions'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import Replay10RoundedIcon from '@mui/icons-material/Replay10Rounded'
import Forward10RoundedIcon from '@mui/icons-material/Forward10Rounded'
import CheckIcon from '@mui/icons-material/Check'
import StyledAudioPlayer from 'styles/styledAudioPlayer'
import { SingleStyledSelect } from 'components/atoms/input/StyledInputs'
import colors from 'styles/colors'
import { useTranslation } from 'react-i18next'

const MonarkAudioPlayer = ({ url, contentActionTracking }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const playerRef = useRef(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const playbackSpeedOptions = Array.from(
    { length: 8 },
    (_, i) => (i + 1) * 0.25
  )

  useEffect(() => {
    setPlaybackSpeed(1)
    setCurrentTime(0)
  }, [url])

  const formatTime = time => {
    if (!time) return '0:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
  }

  const handleSpeedChange = event => {
    const speed = parseFloat(event.target.value)
    setPlaybackSpeed(speed)

    // Update the playback rate of the audio player
    if (playerRef.current?.audio?.current) {
      playerRef.current.audio.current.playbackRate = speed
    }
  }

  const RenderControls = () => {
    const menuSx = {
      width: 160,
      paddingX: 8,
      paddingY: 4,
      justifyContent: 'space-between',
      color: colors.Grey400,
      fontSize: 15,
      fontWeight: 500,
      '&.Mui-selected': {
        backgroundColor: colors.Transparent,
        color: colors.MidnightTeal400
      }
    }
    const timeSx = {
      color: colors.Grey,
      fontSize: 14,
      fontWeight: 500
    }
    return (
      <RowStack spacing={0} justifyContent={'space-between'} flex={1}>
        <RowStack spacing={2} flex={1} >
          <Typography sx={timeSx}>{formatTime(currentTime)}</Typography>
          <Typography sx={timeSx}> / </Typography>
          <Typography sx={timeSx}>{formatTime(duration)}</Typography>
        </RowStack>

        <SingleStyledSelect
          value={playbackSpeed}
          onChange={handleSpeedChange}
          renderValue={selected => {
            return (
              <Typography
                variant='h2'
                sx={{
                  color: colors.Grey,
                  fontWeight: 500
                }}>
                {`${selected}X`}
              </Typography>
            )
          }}
          sx={{
            marginRight: 12
          }}
          MenuProps={{
            PaperProps: {
              style: {
                borderRadius: 12
              }
            }
          }}>
          <MenuItem value={''} disabled sx={menuSx}>
            {t('stepNote.playbackSpeed')}
          </MenuItem>
          {playbackSpeedOptions.map((speed, index) => (
            <MenuItem value={speed} key={`model-${index}`} sx={menuSx}>
              {speed === 1 ? t('stepNote.normal') : `${speed}X`}

              {speed === playbackSpeed && <CheckIcon />}
            </MenuItem>
          ))}
        </SingleStyledSelect>
      </RowStack>
    )
  }

  return (
    <BoxWithBackground
      sx={{
        background: theme.palette.primary.main,
        borderRadius: 2,
        padding: 8,
        paddingX: 12,
        gap: 8,
        width: '100%',
        position: 'sticky',
        bottom: 0,
        zIndex: 4
      }}>
      <StyledAudioPlayer style={{ width: '100%' }}>
        <AudioPlayer
          ref={playerRef}
          src={url}
          autoPlayAfterSrcChange={false}
          layout='stacked-reverse'
          customAdditionalControls={[]}
          showSkipControls={false}
          showFilledVolume={true}
          progressJumpStep={10000}
          customProgressBarSection={[RHAP_UI.PROGRESS_BAR]}
          customControlsSection={[
            RenderControls(),
            RHAP_UI.MAIN_CONTROLS,
            RHAP_UI.VOLUME_CONTROLS
          ]}
          onLoadedMetaData={event => {
            setDuration(event.target.duration)
          }}
          onListen={event => {
            setCurrentTime(event.target.currentTime)
          }}
          customIcons={{
            play: <PlayCircleIcon />,
            rewind: <Replay10RoundedIcon sx={{ color: colors.Grey }} />,
            forward: <Forward10RoundedIcon sx={{ color: colors.Grey }} />,
            volume: <VolumeUpIcon sx={{ fontSize: 24, color: colors.Grey }} />,
            volumeMute: <VolumeOffIcon sx={{ fontSize: 24, color: colors.Grey }} />
          }}
          onPlay={() => {
            logScreenAction('play_audio', null, trackingSubScreenKey)
            contentActionTracking &&
              contentActionTracking('Media Play', { mediaType: 'audio' })
          }}
          onPause={() =>
            logScreenAction('pause_audio', null, trackingSubScreenKey)
          }
        />
      </StyledAudioPlayer>
    </BoxWithBackground>
  )
}

export default MonarkAudioPlayer
